import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styles from "../styles/pages/TrackPackagePage.module.css";
import btnStyles from "../styles/components/Button.module.css";
import Nav from "../components/app/Nav";
import SEO from "../components/app/SEO";
import Footer from "../components/app/Footer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faBoxOpen,
  faCheckCircle,
  faMapMarkerAlt,
  faSpinner,
  faExclamationTriangle,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import {
  orderService,
  GetOrderHistoryResponse,
  ExternalReferenceTrack,
} from "../services/order.service";

const TrackPackagePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const trackingFromUrl = searchParams.get("tracking") || "";
  const [trackingNumber, setTrackingNumber] = useState(trackingFromUrl);
  const [orderHistory, setOrderHistory] = useState<
    GetOrderHistoryResponse["data"] | null
  >(null);
  const [externalRefTrack, setExternalRefTrack] =
    useState<ExternalReferenceTrack | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<
    "carrier" | "external" | null
  >("carrier");
  const externalSectionRef = useRef<HTMLDivElement>(null);
  const carrierSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (activeSection === "external" && externalSectionRef.current) {
      externalSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    } else if (activeSection === "carrier" && carrierSectionRef.current) {
      carrierSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [activeSection]);

  useEffect(() => {
    if (trackingFromUrl) {
      fetchOrderHistory(trackingFromUrl);
    }
  }, [trackingFromUrl]);

  const fetchOrderHistory = async (tracking: string) => {
    setLoading(true);
    setError(null);
    setExternalRefTrack(null);
    try {
      const response = await orderService.getOrderHistory(tracking);
      setOrderHistory(response.data);
      if (response.externalReferenceTrack) {
        setExternalRefTrack(response.externalReferenceTrack);
        setActiveSection("external");
      } else {
        setActiveSection("carrier");
      }
    } catch (err: any) {
      console.error("Error fetching order history:", err);
      if (err.response?.status === 404) {
        setError("No se encontró ninguna orden con este número de rastreo.");
      } else {
        setError(
          "Ocurrió un error al buscar la orden. Por favor, intenta de nuevo.",
        );
      }
      setOrderHistory(null);
    } finally {
      setLoading(false);
    }
  };

  const handleTrack = (e: React.FormEvent) => {
    e.preventDefault();
    if (trackingNumber.trim()) {
      setSearchParams({ tracking: trackingNumber.trim() });
      fetchOrderHistory(trackingNumber.trim());
    }
  };

  return (
    <>
      <SEO
        title="Rastreo de Envíos en Tiempo Real | elPaket"
        description="Rastrea tu paquete en tiempo real con el código de seguimiento de elPaket o número de guía UPS."
        canonicalUrl="https://elpaket.com/track"
      />
      <Nav />
      <main className={`${styles.mainContainer} min-vh-100`}>
        <div className={`${styles.container} py-5`}>
          <div className={styles.trackingContent}>
            <div className={styles.searchCard}>
              <form onSubmit={handleTrack}>
                <label className={styles.searchLabel}>
                  Número de Guía / Tracking
                </label>
                <div className={styles.searchRow}>
                  <input
                    type="text"
                    className={styles.searchInput}
                    placeholder="Ej: TVS12345678"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                  />
                  <button
                    type="submit"
                    className={btnStyles.primary}
                    disabled={loading}
                  >
                    <FontAwesomeIcon
                      icon={loading ? faSpinner : faSearch}
                      spin={loading}
                    />
                    {loading ? "Buscando..." : "Rastrear"}
                  </button>
                </div>
              </form>
            </div>

            {loading && trackingFromUrl ? (
              <div className={styles.resultsPlaceholder}>
                <div className={styles.placeholderIcon}>
                  <FontAwesomeIcon icon={faSpinner} spin />
                </div>
                <p>Cargando información del paquete...</p>
              </div>
            ) : error ? (
              <div className={styles.resultsPlaceholder}>
                <div className={styles.placeholderIcon}>
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    style={{ color: "#f59e0b" }}
                  />
                </div>
                <p>{error}</p>
              </div>
            ) : orderHistory ? (
              <div className={styles.resultsCard}>
                <div className={styles.resultHeader}>
                  <div>
                    <span className={styles.searchLabel}>
                      Número de Guía / Tracking
                    </span>
                    <div className={styles.trackingValue}>
                      {orderHistory.tracking}
                    </div>
                    <div className={styles.statusInfo}>
                      <div className={styles.statusBadge}>
                        <FontAwesomeIcon icon={faMapMarkerAlt} />{" "}
                        {orderHistory.lastEventStatus ?? "—"}
                      </div>
                      {orderHistory.lastEventDate && (
                        <div className={styles.lastUpdate}>
                          Última actualización:{" "}
                          {new Date(orderHistory.lastEventDate).toLocaleDateString()} -{" "}
                          {new Date(orderHistory.lastEventDate).toLocaleTimeString(
                            [],
                            { hour: "2-digit", minute: "2-digit" },
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {externalRefTrack && (
                  <div
                    ref={externalSectionRef}
                    style={{
                      marginBottom: "24px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "12px",
                      overflow: "hidden",
                      background: "#ffffff",
                    }}
                  >
                    <div
                      onClick={() =>
                        setActiveSection(
                          activeSection === "external" ? null : "external",
                        )
                      }
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "16px 20px",
                        backgroundColor: "#f8fafc",
                        cursor: "pointer",
                        borderBottom:
                          activeSection === "external"
                            ? "1px solid #e2e8f0"
                            : "none",
                        userSelect: "none",
                      }}
                    >
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <span
                          className={styles.searchLabel}
                          style={{
                            margin: 0,
                            fontSize: "13px",
                            color: "#1e293b",
                          }}
                        >
                          Seguimiento {externalRefTrack.serviceName}
                        </span>
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#64748b",
                            marginTop: "2px",
                            fontWeight: 500,
                          }}
                        >
                          Referencia: {externalRefTrack.externalReference}
                        </span>
                      </div>
                      <FontAwesomeIcon
                        icon={
                          activeSection === "external"
                            ? faChevronUp
                            : faChevronDown
                        }
                        style={{ color: "#64748b" }}
                      />
                    </div>

                    {activeSection === "external" && (
                      <div style={{ padding: "20px" }}>
                        <div className={styles.timeline}>
                          {externalRefTrack.events.map((event, index) => (
                            <div key={index} className={styles.timelineItem}>
                              <div className={styles.timelineDot}>
                                <FontAwesomeIcon
                                  icon={faCheckCircle}
                                  color={
                                    index === 0
                                      ? "var(--color-primary)"
                                      : "#cbd5e1"
                                  }
                                />
                              </div>
                              <div className={styles.timelineContent}>
                                <div className={styles.statusTitle}>
                                  {event.event}
                                </div>
                                {event.location && (
                                  <div className={styles.statusLocation}>
                                    <FontAwesomeIcon
                                      icon={faMapMarkerAlt}
                                      size="xs"
                                    />
                                    {event.location}
                                  </div>
                                )}
                                <div className={styles.statusDate}>
                                  {event.date}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div
                  ref={carrierSectionRef}
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "12px",
                    overflow: "hidden",
                    background: "#ffffff",
                  }}
                >
                  <div
                    onClick={() =>
                      setActiveSection(
                        activeSection === "carrier" ? null : "carrier",
                      )
                    }
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "16px 20px",
                      backgroundColor: "#f8fafc",
                      cursor: "pointer",
                      borderBottom:
                        activeSection === "carrier"
                          ? "1px solid #e2e8f0"
                          : "none",
                      userSelect: "none",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span
                        className={styles.searchLabel}
                        style={{
                          margin: 0,
                          fontSize: "13px",
                          color: "#1e293b",
                        }}
                      >
                        Seguimiento {orderHistory.carrierName}
                      </span>
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#64748b",
                          marginTop: "2px",
                          fontWeight: 500,
                        }}
                      >
                        Referencia: {orderHistory.carrierTracking}
                      </span>
                    </div>
                    <FontAwesomeIcon
                      icon={
                        activeSection === "carrier"
                          ? faChevronUp
                          : faChevronDown
                      }
                      style={{ color: "#64748b" }}
                    />
                  </div>

                  {activeSection === "carrier" && (
                    <div style={{ padding: "20px" }}>
                      <div className={styles.timeline}>
                        {orderHistory.events.map((event, index) => (
                          <div key={index} className={styles.timelineItem}>
                            <div className={styles.timelineDot}>
                              <FontAwesomeIcon
                                icon={faCheckCircle}
                                color="var(--color-primary)"
                              />
                            </div>
                            <div className={styles.timelineContent}>
                              <div className={styles.statusTitle}>
                                {event.eventStatus}
                              </div>
                              <div className={styles.statusDate}>
                                {new Date(event.date).toLocaleDateString()} -{" "}
                                {new Date(event.date).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className={styles.resultsPlaceholder}>
                <div className={styles.placeholderIcon}>
                  <FontAwesomeIcon icon={faBoxOpen} />
                </div>
                <p>
                  Ingresa un número de rastreo para ver el historial de tu
                  paquete.
                </p>
                <p className="small">
                  Mantente informado sobre cada paso de tu envío.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
};

export default TrackPackagePage;
